(() => {
  const STORAGE_KEY = 'divine-blossom-wishlist';

  const Wishlist = {
    items: [],

    init() {
      this.items = this.read();
      this.bindEvents();
      this.syncUI();
      this.renderPages();
    },

    read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },

    write() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      this.syncUI();
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: this.items } }));
    },

    has(id) {
      return this.items.some((item) => String(item.id) === String(id));
    },

    add(product) {
      if (!product || !product.id || this.has(product.id)) return;
      this.items.unshift(product);
      this.write();
    },

    remove(id) {
      this.items = this.items.filter((item) => String(item.id) !== String(id));
      this.write();
    },

    toggle(product) {
      if (this.has(product.id)) {
        this.remove(product.id);
        return false;
      }
      this.add(product);
      return true;
    },

    productFromButton(button) {
      return {
        id: button.dataset.productId,
        handle: button.dataset.productHandle,
        url: button.dataset.productUrl,
        title: button.dataset.productTitle,
        image: button.dataset.productImage || '',
        price: Number(button.dataset.productPrice || 0),
        compareAtPrice: Number(button.dataset.productCompareAtPrice || 0),
        available: button.dataset.productAvailable !== 'false'
      };
    },

    bindEvents() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-wishlist-button]');
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();

        const product = this.productFromButton(button);
        if (!product.id) return;

        const isActive = this.toggle(product);
        this.updateButton(button, isActive);
        this.renderPages();
      });

      document.addEventListener('wishlist:updated', () => {
        this.syncUI();
      });

      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY) {
          this.items = this.read();
          this.syncUI();
          this.renderPages();
        }
      });
    },

    updateButton(button, isActive) {
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      const addLabel = (window.theme && theme.strings && theme.strings.wishlistAdd) || 'Add to wishlist';
      const removeLabel = (window.theme && theme.strings && theme.strings.wishlistRemove) || 'Remove from wishlist';
      button.setAttribute('aria-label', isActive ? removeLabel : addLabel);

      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = isActive ? removeLabel : addLabel;

      const outline = button.querySelector('.wishlist-button__icon--outline');
      const filled = button.querySelector('.wishlist-button__icon--filled');
      if (outline) {
        outline.hidden = isActive;
        outline.style.display = isActive ? 'none' : 'inline-flex';
      }
      if (filled) {
        filled.hidden = !isActive;
        filled.style.display = isActive ? 'inline-flex' : 'none';
      }
    },

    syncUI() {
      document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
        this.updateButton(button, this.has(button.dataset.productId));
      });

      const count = this.items.length;
      document.documentElement.classList.toggle('wishlist-has-items', count > 0);
      if (count > 0) {
        document.documentElement.setAttribute('data-wishlist-count', String(count));
      } else {
        document.documentElement.removeAttribute('data-wishlist-count');
      }

      document.querySelectorAll('[data-wishlist-header]').forEach((link) => {
        link.classList.toggle('is-active', count > 0);
        const countEl = link.querySelector('[data-wishlist-count]');
        if (countEl) countEl.textContent = count > 0 ? String(count) : '';
      });
    },

    formatMoney(cents) {
      const format =
        (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) ||
        '${{amount}}';
      const value = (Number(cents) || 0) / 100;
      const amount = value.toFixed(2);
      const amountNoDecimals = String(Math.round(value));
      const amountWithComma = amount.replace('.', ',');

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    },

    async renderPages() {
      const roots = document.querySelectorAll('[data-wishlist-root]');
      if (!roots.length) return;

      await Promise.all([...roots].map((root) => this.renderRoot(root)));
    },

    async renderRoot(root) {
      const loading = root.querySelector('[data-wishlist-loading]');
      const empty = root.querySelector('[data-wishlist-empty]');
      const grid = root.querySelector('[data-wishlist-grid]');
      if (!grid) return;

      if (loading) loading.hidden = false;
      if (empty) empty.hidden = true;
      grid.hidden = true;
      grid.innerHTML = '';

      if (!this.items.length) {
        if (loading) loading.hidden = true;
        if (empty) empty.hidden = false;
        return;
      }

      const cards = await Promise.all(
        this.items.map(async (item) => {
          try {
            const response = await fetch(`/products/${item.handle}.js`);
            if (!response.ok) throw new Error('Product unavailable');
            const product = await response.json();
            return this.buildCard({
              id: product.id,
              handle: product.handle,
              url: `/products/${product.handle}`,
              title: product.title,
              image: product.featured_image || item.image || '',
              price: product.price,
              compareAtPrice: product.compare_at_price || 0,
              available: product.available
            });
          } catch (e) {
            return this.buildCard(item);
          }
        })
      );

      grid.innerHTML = cards.join('');
      if (loading) loading.hidden = true;
      empty && (empty.hidden = true);
      grid.hidden = false;
      this.syncUI();
    },

    buildCard(product) {
      const image = product.image
        ? `<img src="${product.image}" alt="${this.escape(product.title)}" loading="lazy" width="600" height="600">`
        : `<div class="wishlist-card__placeholder">No image</div>`;

      const compare =
        product.compareAtPrice > product.price
          ? `<s>${this.formatMoney(product.compareAtPrice)}</s>`
          : '';

      const addLabel = (window.theme && theme.strings && theme.strings.wishlistAdd) || 'Add to wishlist';
      const removeLabel = (window.theme && theme.strings && theme.strings.wishlistRemove) || 'Remove from wishlist';

      return `
        <article class="wishlist-card" data-product-id="${product.id}">
          <div class="wishlist-card__media">
            <a href="${product.url}">${image}</a>
            <button
              type="button"
              class="wishlist-button wishlist-button--card is-active"
              style="display:inline-flex;align-items:center;justify-content:center;padding:0;border:0;line-height:0;cursor:pointer;appearance:none;-webkit-appearance:none;position:absolute;top:10px;right:10px;bottom:auto;left:auto;z-index:5;width:36px;height:36px;margin:0;border-radius:50%;background:rgba(255,255,255,0.92);box-shadow:0 1px 3px rgba(0,0,0,0.08);color:#111;"
              data-wishlist-button
              data-product-id="${product.id}"
              data-product-handle="${this.escape(product.handle)}"
              data-product-url="${product.url}"
              data-product-title="${this.escape(product.title)}"
              data-product-image="${product.image || ''}"
              data-product-price="${product.price || 0}"
              data-product-compare-at-price="${product.compareAtPrice || 0}"
              data-product-available="${product.available !== false}"
              aria-label="${removeLabel}"
              aria-pressed="true"
            >
              <span class="wishlist-button__icon wishlist-button__icon--outline" hidden style="display:none;align-items:center;justify-content:center;">
                <svg class="icon icon-heart icon-heart--outline" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" fill="none">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" fill="none"/>
                </svg>
              </span>
              <span class="wishlist-button__icon wishlist-button__icon--filled" style="display:inline-flex;align-items:center;justify-content:center;">
                <svg class="icon icon-heart icon-heart--filled" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </span>
            </button>
          </div>
          <h3 class="wishlist-card__title"><a href="${product.url}">${this.escape(product.title)}</a></h3>
          <p class="wishlist-card__price">${compare}<span>${this.formatMoney(product.price)}</span></p>
        </article>
      `;
    },

    escape(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  };

  window.DivineWishlist = Wishlist;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Wishlist.init());
  } else {
    Wishlist.init();
  }
})();
